import { forwardRef, useImperativeHandle, useRef } from 'react';
import { Point } from '../../../shared/types/mathTypes.ts';
import { Marker, Position } from '../../../shared/types/protocol.ts';
import { PositionSelection } from '../../types/positionSelection.ts';
import { ArrowMarker, ArrowMarkerRef } from './arrowMarker.tsx';
import { CircleMarker, CircleMarkerRef } from './circleMarker.tsx';


export interface PositionMarkersRef {
    updatePositions: () => void;
}

interface Props {
    positions?: Position[];
    visiblePositionIds: ReadonlySet<number>;
    positionSelection: PositionSelection | null;
    showSelectedPosNr?: boolean;
    showAllMarkers?: boolean;
    showAllPosNrs?: boolean;
    page: number;
    pdfToScreenCoords: (pdfCoords: Point) => Point;
}

export const PositionMarkers = forwardRef<PositionMarkersRef, Props>(({
    positions,
    visiblePositionIds,
    positionSelection,
    showSelectedPosNr,
    showAllMarkers,
    showAllPosNrs,
    page,
    pdfToScreenCoords,
}, ref) => {
    const markerRefs = useRef(new Map<string, CircleMarkerRef | ArrowMarkerRef | null>(null));


    useImperativeHandle(ref, () => ({
        updatePositions,
    }));

    function updatePositions() {
        markerRefs.current.forEach((ref) => {
            ref?.updatePosition();
        });
    }


    return (
        <>
            {positions?.map((position, positionIdx) => {
                const isVisible = visiblePositionIds.has(position.id);
                if (!isVisible) return null;

                const markers = position.markers.map((marker, markerIdx) =>
                    <PositionMarker
                        key={positionIdx + '-' + markerIdx}
                        markerIdx={markerIdx}
                        positionIdx={positionIdx}
                        position={position}
                        marker={marker}
                        page={page}
                        positionSelection={positionSelection}
                        showSelectedPosNr={showSelectedPosNr}
                        showAllMarkers={showAllMarkers}
                        showAllPosNrs={showAllPosNrs}
                        pdfToScreenCoords={(pdfCoords) => pdfToScreenCoords(pdfCoords)}
                        setRef={(ref) => markerRefs.current.set(positionIdx + '-' + markerIdx, ref)}
                    />
                );
                return markers;
            })}
        </>
    );
});


function PositionMarker({
    markerIdx,
    positionIdx,
    position,
    marker,
    page,
    positionSelection,
    showSelectedPosNr,
    showAllMarkers,
    showAllPosNrs,
    pdfToScreenCoords,
    setRef,
}: {
    markerIdx: number,
    positionIdx: number,
    position: Position,
    marker: Marker,
    page: number,
    positionSelection: PositionSelection | null,
    showSelectedPosNr?: boolean,
    showAllMarkers?: boolean,
    showAllPosNrs?: boolean,
    pdfToScreenCoords: (pdfCoords: Point) => Point,
    setRef: (ref: CircleMarkerRef | ArrowMarkerRef | null) => void,
}) {

    const isSelectedOptimistic = positionIdx === positionSelection?.positionIdx && (
        markerIdx === positionSelection?.markerIdx || positionSelection?.markerIdx == null
    );
    const isVisible = position.page === page && (isSelectedOptimistic || showAllMarkers || showAllPosNrs);
    const isCircle = isVisible && marker.x2 == null;
    const isArrow = isVisible && marker.x2 != null && marker.y2 != null;
    const drawMarker = showAllMarkers || isSelectedOptimistic;
    const drawPositionNumber = !!showAllPosNrs || !!showSelectedPosNr && isSelectedOptimistic;
    const showDescription = isVisible && isSelectedOptimistic;

    return (
        <>
            {isCircle &&
                <CircleMarker
                    ref={(ref) => setRef(ref)}
                    positionNumber={position.positionNumber}
                    pdfPosition={{ x: marker.x, y: marker.y }}
                    pdfToScreenCoords={pdfToScreenCoords}
                    sizeFactor={marker.sizeFactor}
                    drawMarker={drawMarker}
                    drawPositionNumber={drawPositionNumber}
                    description={showDescription ? marker.text : undefined}
                />
            }
            {isArrow &&
                <ArrowMarker
                    ref={(ref) => setRef(ref)}
                    positionNumber={position.positionNumber}
                    pdfStart={{ x: marker.x, y: marker.y }}
                    pdfEnd={{ x: marker.x2!, y: marker.y2! }}
                    pdfToScreenCoords={pdfToScreenCoords}
                    drawMarker={drawMarker}
                    drawPositionNumber={drawPositionNumber}
                    description={showDescription ? marker.text : undefined}
                />
            }
        </>
    );
}
